import { Button } from "@/components/ui/button";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { convertDateToText, convertToTimeFormat } from "@/constants";
import { UserContext } from "@/context/User/UserContext";
import { Action, deleteEvent } from "@/services/Case.service";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { BiCalendar, BiTagAlt, BiUser } from "react-icons/bi";
import { CgNotes } from "react-icons/cg";
import { RiAttachment2 } from "react-icons/ri";
import { toast } from "sonner";
import AttachmentDetails from "../AttachmentDetails";

type Props = {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	action: Action;
};

const DisplayEvent: React.FC<Props> = ({ open, setOpen, action }) => {
	const user = useContext(UserContext);
	const queryClient = useQueryClient();

	const eventDelete = useMutation({
		mutationKey: ["event_delete"],
		mutationFn: (data: number) => deleteEvent(user?.accessToken!, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["case", "actions", action.actionid],
				exact: true,
				refetchType: "all",
			});
			setOpen(false);
			toast.success("Success", {
				description: "Event Deleted",
			});
			eventDelete.reset();
		},
		onError: (err) => {
			toast.error("Error!", {
				description: "Error deleting event, try again",
			});
			console.log(err);
		},
	});

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetContent className="sm:max-w-2xl">
				<SheetHeader>
					<SheetTitle className="text-3xl">
						<span className="text-gray-600 font-bold mr-2">{`#${action.actionid}`}</span>
						<span className="font-semibold">
							{convertDateToText(action.actiondate)}
						</span>
					</SheetTitle>
				</SheetHeader>
				<div className="grid py-4 gap-4">
					<div className="grid grid-cols-4 gap-y-4 gap-x-1">
						<div className="col-span-1 flex gap-6">
							<span className="inline-flex items-center font-light text-gray-600">
								<BiTagAlt className="text-lg mr-2" />
								Event Type
							</span>
						</div>
						<div className="col-span-3">
							<div className="flex-1 w-5/6">
								<span className="text-sm text-blue-700 bg-blue-100 border border-blue-300 rounded-sm font-semibold px-2 py-1 self-start">
									{action.actiontype}
								</span>
							</div>
						</div>
						<div className="col-span-1 flex flex-col gap-4">
							<span className="inline-flex items-center font-light text-gray-600">
								<BiCalendar className="text-lg mr-2" />
								Created
							</span>
						</div>
						<div className="col-span-3">
							<span>
								{convertDateToText(action.createdon)}{" "}
								{convertToTimeFormat(action.createdat)}
							</span>
						</div>
						<div className="col-span-1 flex flex-col gap-4">
							<span className="inline-flex items-center font-light text-gray-600">
								<BiUser className="text-lg mr-2" />
								Created By
							</span>
						</div>
						<div className="col-span-3">
							<span>{action.username}</span>
						</div>
						{action.nextactiondate && action.nextactiontype ? (
							<>
								<div className="col-span-1 flex flex-col gap-4">
									<span className="inline-flex items-center font-light text-gray-600">
										<BiCalendar className="text-lg mr-2" />
										Next Action Date
									</span>
								</div>
								<div className="col-span-3">
									<span>{convertDateToText(action.nextactiondate)}</span>
								</div>
								<div className="col-span-1 flex flex-col gap-4">
									<span className="inline-flex items-center font-light text-gray-600">
										<BiTagAlt className="text-lg mr-2" />
										Next Action Type
									</span>
								</div>
								<div className="col-span-3">
									<span>{action.nextactiontype}</span>
								</div>
							</>
						) : null}
						<div className="col-span-1 flex flex-col gap-4">
							<span className="inline-flex items-center font-light text-gray-600">
								<CgNotes className="text-lg mr-2" />
								Description
							</span>
						</div>
						<div className="col-span-4">
							<span>{action.actiondescription}</span>
						</div>
						{action.attachments ? (
							<>
								<div className="col-span-1 flex flex-col gap-4">
									<span className="inline-flex items-center font-light text-gray-600">
										<RiAttachment2 className="text-lg mr-2" />
										Attachments
									</span>
								</div>
								<div className="col-span-4">
									<AttachmentDetails attachments={action.attachments} />
								</div>
							</>
						) : null}
					</div>
				</div>

				<Button
					className="mt-4 w-full"
					onClick={() => eventDelete.mutate(action.actionid)}
					variant="destructive"
					disabled={eventDelete.isPending}
				>
					Delete Event
				</Button>
				{/* <SheetFooter className="sm:justify-start">
					<SheetClose asChild>
						<Button type="submit">Done</Button>
					</SheetClose>
				</SheetFooter> */}
			</SheetContent>
		</Sheet>
	);
};

export default DisplayEvent;
